import React, { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Card, Button, Alert, Spinner, Row, Col, Badge, Table, Form } from 'react-bootstrap'
import { useAdminSection } from 'api/hooks/use-admin-sections'
import { useQueryClient } from 'react-query'
import AdminTabs from 'admin/shared/AdminTabs'
import AnalyticModal from './AnalyticModal'
import TableColumnModal from './TableColumnModal'

const SectionDetail = () => {
  const { id } = useParams()
  const queryClient = useQueryClient()
  const { data: section, isLoading, error } = useAdminSection({ id })
  // The primary date / amount are stored as column ids; show the column's name.
  const columnName = (columnId) =>
    section?.table_columns?.find((col) => col.id === columnId)?.name || 'N/A'
  const [activeTab, setActiveTab] = useState('details')
  const [showOffAnalytics, setShowOffAnalytics] = useState(false)

  const [analyticModal, setAnalyticModal] = useState({ id: null, mode: null })
  const [tableColumnModal, setTableColumnModal] = useState({ id: null, mode: null })

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  if (error) {
    return <Alert variant="danger">Error loading module: {error.message}</Alert>
  }

  if (!section) {
    return <Alert variant="warning">Module not found.</Alert>
  }

  const filteredAnalytics = section.analytics?.filter((a) => showOffAnalytics || a.is_core) || []

  const handleSaved = () => {
    queryClient.invalidateQueries(['admin-section', id])
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{section.name}</h2>
        <div>
          <Button as={Link} to={`/admin/sections/${id}/edit`} variant="warning" className="me-2">
            Edit Module
          </Button>
          <Button as={Link} to="/admin/sections" variant="light-primary">
            Back to List
          </Button>
        </div>
      </div>

      <AdminTabs
        tabs={[
          { name: 'Details', value: 'details' },
          { name: `Analytics (${filteredAnalytics.length})`, value: 'analytics' },
          { name: `Table Columns (${section.table_columns?.length || 0})`, value: 'table-columns' }
        ]}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      {activeTab === 'details' && (
        <Row>
          <Col md={6}>
            <Card className="mb-4">
              <Card.Header><h5>Basic Information</h5></Card.Header>
              <Card.Body>
                <dl className="row mb-0">
                  <dt className="col-sm-5">Name:</dt>
                  <dd className="col-sm-7">{section.name}</dd>
                  <dt className="col-sm-5">Abbreviation:</dt>
                  <dd className="col-sm-7"><code>{section.abbreviation}</code></dd>
                  <dt className="col-sm-5">Parent Module:</dt>
                  <dd className="col-sm-7">
                    {section.parent_module ? <Badge bg="info">{section.parent_module}</Badge> : 'N/A'}
                  </dd>
                  <dt className="col-sm-5">Folder:</dt>
                  <dd className="col-sm-7">{section.folder || 'N/A'}</dd>
                  <dt className="col-sm-5">Unit:</dt>
                  <dd className="col-sm-7">{section.unit || 'N/A'}</dd>
                </dl>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="mb-4">
              <Card.Header><h5>Display Settings</h5></Card.Header>
              <Card.Body>
                <dl className="row mb-0">
                  <dt className="col-sm-5">Display Key:</dt>
                  <dd className="col-sm-7">{section.display_key || 'N/A'}</dd>
                  <dt className="col-sm-5">Display Key Two:</dt>
                  <dd className="col-sm-7">{section.display_key_two || 'N/A'}</dd>
                  <dt className="col-sm-5">Date Field:</dt>
                  <dd className="col-sm-7">{section.date_field || 'N/A'}</dd>
                  <dt className="col-sm-5">Amount Field:</dt>
                  <dd className="col-sm-7">{section.amount_field || 'N/A'}</dd>
                  <dt className="col-sm-5">Primary Date Column:</dt>
                  <dd className="col-sm-7">{columnName(section.primary_date_column_id)}</dd>
                  <dt className="col-sm-5">Primary Amount Column:</dt>
                  <dd className="col-sm-7">{columnName(section.primary_amount_column_id)}</dd>
                </dl>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {activeTab === 'analytics' && (
        <Card>
          <Card.Body>
            <div className="d-flex justify-content-end mb-3">
              <Form.Check
                type="switch"
                label="Show Off Analytics"
                checked={showOffAnalytics}
                onChange={(e) => setShowOffAnalytics(e.target.checked)}
              />
            </div>
            {filteredAnalytics.length > 0 ? (
              <Table bordered hover responsive>
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAnalytics.map((analytic) => (
                    <tr key={analytic.id}>
                      <td><code>{analytic.key}</code></td>
                      <td>{analytic.name}</td>
                      <td><span className="text-muted">{analytic.is_core ? 'On' : 'Off'}</span></td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <Button variant="info" size="sm" className="me-2" onClick={() => setAnalyticModal({ id: analytic.id, mode: 'view' })}>View</Button>
                        <Button variant="warning" size="sm" onClick={() => setAnalyticModal({ id: analytic.id, mode: 'edit' })}>Edit</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Alert variant="info" className="mb-0">No analytics for this module.</Alert>
            )}
          </Card.Body>
        </Card>
      )}

      {activeTab === 'table-columns' && (
        <Card>
          <Card.Body>
            {section.table_columns?.length > 0 ? (
              <Table bordered hover responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Database Column</th>
                    <th>Filter Type</th>
                    <th>Grouping</th>
                    <th>Default</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {section.table_columns.map((tc) => (
                    <tr key={tc.id}>
                      <td>{tc.name}</td>
                      <td><code>{tc.database_column_name}</code></td>
                      <td><span className="text-muted">{tc.filter_type || '-'}</span></td>
                      <td><span className="text-muted">{tc.grouping || '-'}</span></td>
                      <td><span className="text-muted">{tc.is_default ? 'Yes' : 'No'}</span></td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <Button variant="info" size="sm" className="me-2" onClick={() => setTableColumnModal({ id: tc.id, mode: 'view' })}>View</Button>
                        <Button variant="warning" size="sm" onClick={() => setTableColumnModal({ id: tc.id, mode: 'edit' })}>Edit</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Alert variant="info" className="mb-0">No table columns for this module.</Alert>
            )}
          </Card.Body>
        </Card>
      )}

      {analyticModal.id && (
        <AnalyticModal
          analyticId={analyticModal.id}
          mode={analyticModal.mode}
          onClose={() => setAnalyticModal({ id: null, mode: null })}
          onSaved={handleSaved}
        />
      )}

      {tableColumnModal.id && (
        <TableColumnModal
          tableColumnId={tableColumnModal.id}
          mode={tableColumnModal.mode}
          onClose={() => setTableColumnModal({ id: null, mode: null })}
          onSaved={handleSaved}
        />
      )}
    </div>
  )
}

export default SectionDetail
